<script setup lang="ts">
interface Props {
  title?: string
  subtitle?: string
  icon?: string
  iconColor?: string
  compact?: boolean
}

withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  icon: '',
  iconColor: 'primary',
  compact: false,
})
</script>

<template>
  <div
    class="page-title-section"
    :class="{ 'page-title-compact': compact }"
  >
    <VContainer>
      <div class="page-title-content">
        <!-- Title with Icon -->
        <div class="title-with-icon">
          <!-- Icon (optional) -->
          <VIcon
            v-if="icon"
            :icon="icon"
            :color="iconColor"
            size="48"
            class="me-4"
          />

          <!-- Title -->
          <h1
            v-if="title"
            class="text-h3 text-md-h2 font-weight-bold mb-2"
          >
            {{ title }}
          </h1>
        </div>

        <!-- Subtitle -->
        <p
          v-if="subtitle"
          style="margin-block-end: 0;"
          class="text-body-1 text-medium-emphasis"
        >
          {{ subtitle }}
        </p>

        <!-- Custom slot for additional content -->
        <slot />
      </div>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.page-title-section {
  // border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 100px;
  padding-block: 0;

  .page-title-content {
    text-align: center;

    .title-with-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
    }

    h1 {
      color: rgb(var(--v-theme-on-surface));
      margin-block-end: 0;
    }

    p {
      margin-inline: auto;
      max-inline-size: 800px;
    }
  }
}

@media (max-width: 600px) {
  .page-title-section {
    .page-title-content {
      .title-with-icon {
        gap: 0.5rem;

        .v-icon {
          font-size: 32px !important;
        }
      }

      h1 {
        font-size: 1.375rem !important;
        line-height: 1.4 !important;
      }

      p {
        font-size: 0.875rem !important;
        line-height: 1.4 !important;
      }
    }
  }

  // Compact mode for mobile - even more compact
  .page-title-compact {
    margin-block-start: 65px;
    padding-block: 0.25rem 0.5rem;

    .page-title-content {
      .title-with-icon {
        gap: 0.375rem;

        .v-icon {
          font-size: 24px !important;
          margin-inline-end: 0 !important;
        }
      }

      h1 {
        font-size: 1.125rem !important;
        line-height: 1.3 !important;
        margin-block-end: 0.125rem !important;
      }

      p {
        font-size: 0.6875rem !important;
        line-height: 1.2 !important;
      }
    }
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  // Compact mode for tablets
  .page-title-compact {
    margin-block-start: 80px;
    padding-block: 0.75rem;

    .page-title-content {
      .title-with-icon {
        .v-icon {
          font-size: 32px !important;
          margin-inline-end: 0.75rem !important;
        }
      }

      h1 {
        font-size: 1.5rem !important;
        line-height: 1.4 !important;
        margin-block-end: 0.5rem !important;
      }

      p {
        font-size: 0.875rem !important;
        line-height: 1.4 !important;
      }
    }
  }
}
</style>
